<template>
  <div class="ind-root" :class="themeClass">
    <div class="ambient" aria-hidden="true"></div>

    <header class="ind-header">
      <div class="brand" @click="$router.push('/main-panel')" title="Inicio">
        <img src="@/assets/logogruas.png" alt="GM" class="logo" />
        <div class="brand-text">
          <h1>Indicadores</h1>
          <span class="brand-sub">Tablero operativo · Grúas Monterrey</span>
        </div>
      </div>

      <div class="header-right">
        <button class="btn-refresh" type="button" @click="fetchData" :disabled="loading">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z" fill="currentColor"/>
          </svg>
          {{ loading ? 'Actualizando…' : 'Actualizar' }}
        </button>

        <button
          class="theme-toggle"
          type="button"
          @click="toggleTheme"
          :title="isDark ? 'Modo claro' : 'Modo oscuro'"
        >
          <svg v-if="isDark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4"/>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        </button>
      </div>
    </header>

    <!-- CONTROLS -->
    <div class="controls">
      <label>Fecha inicio: <input type="date" v-model="startDate" /></label>
      <label>Fecha fin: <input type="date" v-model="endDate" /></label>
      <button class="btn-apply" type="button" @click="fetchData" :disabled="loading">Aplicar</button>

      <div class="filters" v-if="filtros">
        <FilterGroup label="Unidad de negocio" :options="filtros.grupos" v-model="selGrupos" />
        <FilterGroup label="Sucursal" :options="filtros.sucursales" v-model="selSucursales" />
        <FilterGroup label="Tipo de servicio" :options="filtros.tipos" v-model="selTipos" />
        <FilterGroup label="Tipo de unidad" :options="filtros.tiposUnidad" v-model="selTiposUnidad" />
      </div>
    </div>

    <!-- KPI HERO -->
    <div class="hero-strip" v-if="kpis">
      <div class="hero-tile"><span class="hero-label">Ventas totales</span><span class="hero-value">{{ abbr(kpis.ventas) }}</span><span class="hero-sub">{{ money(kpis.ventas) }}</span></div>
      <div class="hero-tile"><span class="hero-label">Servicios</span><span class="hero-value">{{ fmtInt(kpis.servicios) }}</span><span class="hero-sub">Órdenes trabajadas</span></div>
      <div class="hero-tile"><span class="hero-label">Ticket promedio</span><span class="hero-value">{{ abbr(kpis.ticket_prom) }}</span><span class="hero-sub">{{ money(kpis.ticket_prom) }}</span></div>
      <div class="hero-tile"><span class="hero-label">SLA ETA</span><span class="hero-value">{{ num1(kpis.sla_eta) }}%</span><span class="hero-sub">ETA prom: {{ num2(kpis.eta_promedio) }}</span></div>
      <div class="hero-tile"><span class="hero-label">Unidades activas</span><span class="hero-value">{{ fmtInt(kpis.unidades) }}</span><span class="hero-sub">Con servicios</span></div>
      <div class="hero-tile"><span class="hero-label">Operadores</span><span class="hero-value">{{ fmtInt(kpis.operadores) }}</span><span class="hero-sub">Con servicios</span></div>
    </div>

    <!-- TABS -->
    <nav class="tabs">
      <button
        v-for="t in menus"
        :key="t.key"
        :class="['tab', { active: t.key === activeMenu }]"
        @click="activeMenu = t.key"
      >{{ t.label }}</button>
    </nav>

    <div v-if="loading && !data" class="state-msg">Cargando indicadores…</div>
    <div v-else-if="errorMsg" class="state-msg error">{{ errorMsg }}</div>
    <div v-else-if="isEmpty" class="state-msg">No hay datos para el periodo / filtros seleccionados.</div>

    <template v-else-if="data">
      <!-- ================= RESUMEN ================= -->
      <section v-show="activeMenu === 'resumen'" class="tab-body">
        <div class="grid grid-3">
          <StatCard title="Ventas por sucursal y tipo"><v-chart :option="ventasSucursalTipoOption" autoresize style="height:460px" /></StatCard>
          <StatCard title="Servicios por sucursal y tipo"><v-chart :option="serviciosSucursalTipoOption" autoresize style="height:460px" /></StatCard>
          <StatCard title="Ticket promedio por sucursal y tipo"><v-chart :option="ticketHeatmapOption" autoresize style="height:460px" /></StatCard>
        </div>
        <div class="grid grid-3">
          <StatCard title="Servicios por día"><v-chart :option="serviciosDiaOption" autoresize style="height:340px" /></StatCard>
          <StatCard title="Servicios por hora - Heatmap"><v-chart :option="heatDiaHoraOption" autoresize style="height:340px" /></StatCard>
          <StatCard title="Servicios por hora y tipo"><v-chart :option="serviciosHoraTipoOption" autoresize style="height:340px" /></StatCard>
        </div>
        <div class="grid grid-2">
          <StatCard title="Servicios por hora (por día)"><v-chart :option="serviciosHoraFechaOption" autoresize style="height:360px" /></StatCard>
        </div>
      </section>

      <!-- ================= OPERACIÓN ================= -->
      <section v-show="activeMenu === 'operacion'" class="tab-body">
        <div class="kpi-cards">
          <div class="mini-kpi"><span>Sol. a asignación</span><strong>{{ num1(op.kpis.sol_asig) }} min</strong><small>Meta &lt; {{ op.metas.sol_asig }}</small></div>
          <div class="mini-kpi"><span>Asignación a inicio</span><strong>{{ num1(op.kpis.asig_ini) }} min</strong><small>Meta &lt; {{ op.metas.asig_ini }}</small></div>
          <div class="mini-kpi"><span>Inicio a arribo</span><strong>{{ num1(op.kpis.ini_arri) }} min</strong><small>Meta &lt; {{ op.metas.ini_arri }}</small></div>
          <div class="mini-kpi"><span>Solicitud a arribo</span><strong>{{ num1(op.kpis.sol_arri) }} min</strong><small>Meta &lt; {{ op.metas.sol_arri }}</small></div>
          <div class="mini-kpi"><span>Tiempo total</span><strong>{{ num1(op.kpis.total) }} min</strong><small>Meta &lt; {{ op.metas.total }}</small></div>
        </div>

        <StatCard title="Tiempos de respuesta por día y turno">
          <div class="metric-select">
            <label>Métrica:
              <select v-model="metricSel">
                <option v-for="(m, k) in op.metric_charts" :key="k" :value="k">{{ m.label }}</option>
              </select>
            </label>
          </div>
          <v-chart :option="tiemposTurnoOption" autoresize style="height:430px" />
        </StatCard>

        <div class="grid grid-2">
          <StatCard title="SLA ETA por hora"><v-chart :option="slaHoraOption" autoresize style="height:430px" /></StatCard>
          <StatCard title="SLA ETA por día y hora"><v-chart :option="slaHeatOption" autoresize style="height:430px" /></StatCard>
        </div>

        <div class="grid grid-2">
          <StatCard title="Operadores por día y turno"><v-chart :option="headOperadoresOption" autoresize style="height:420px" /></StatCard>
          <StatCard title="Unidades por día y turno"><v-chart :option="headUnidadesOption" autoresize style="height:420px" /></StatCard>
        </div>

        <div class="grid grid-2">
          <StatCard title="Operadores: solicitudes, términos y ocupación"><v-chart :option="ocupOperadoresOption" autoresize style="height:430px" /></StatCard>
          <StatCard title="Unidades: solicitudes, términos y ocupación"><v-chart :option="ocupUnidadesOption" autoresize style="height:430px" /></StatCard>
        </div>

        <StatCard title="Detalle por turno y día">
          <div class="table-wrap tall">
            <table>
              <thead><tr>
                <th>Turno</th><th>Fecha</th><th class="num">Servicios</th>
                <th class="num">Sol→Asig</th><th class="num">Asig→Ini</th><th class="num">Ini→Arri</th><th class="num">Sol→Arri</th><th class="num">Total</th>
                <th class="num">SLA S→A</th><th class="num">SLA A→I</th><th class="num">SLA I→A</th><th class="num">SLA S→Arri</th><th class="num">SLA Total</th>
              </tr></thead>
              <tbody>
                <tr v-for="(r, i) in op.tabla_turno" :key="i">
                  <td>{{ r.turno }}</td><td>{{ r.fecha }}</td><td class="num">{{ r.servicios }}</td>
                  <td class="num">{{ minv(r.prom_sol_asig) }}</td><td class="num">{{ minv(r.prom_asig_ini) }}</td><td class="num">{{ minv(r.prom_ini_arri) }}</td><td class="num">{{ minv(r.prom_sol_arri) }}</td><td class="num">{{ minv(r.prom_total) }}</td>
                  <td class="num">{{ pct(r.sla_sol_asig) }}</td><td class="num">{{ pct(r.sla_asig_ini) }}</td><td class="num">{{ pct(r.sla_ini_arri) }}</td><td class="num">{{ pct(r.sla_sol_arri) }}</td><td class="num">{{ pct(r.sla_total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>

        <StatCard title="Promedios generales por turno">
          <div class="table-wrap">
            <table>
              <thead><tr>
                <th>Turno</th><th class="num">Servicios</th>
                <th class="num">Sol→Asig</th><th class="num">Asig→Ini</th><th class="num">Ini→Arri</th><th class="num">Sol→Arri</th><th class="num">Total</th>
                <th class="num">SLA S→A</th><th class="num">SLA A→I</th><th class="num">SLA I→A</th><th class="num">SLA S→Arri</th><th class="num">SLA Total</th>
              </tr></thead>
              <tbody>
                <tr v-for="(r, i) in op.resumen_turno" :key="i">
                  <td>{{ r.turno }}</td><td class="num">{{ r.servicios }}</td>
                  <td class="num">{{ minv(r.prom_sol_asig) }}</td><td class="num">{{ minv(r.prom_asig_ini) }}</td><td class="num">{{ minv(r.prom_ini_arri) }}</td><td class="num">{{ minv(r.prom_sol_arri) }}</td><td class="num">{{ minv(r.prom_total) }}</td>
                  <td class="num">{{ pct(r.sla_sol_asig) }}</td><td class="num">{{ pct(r.sla_asig_ini) }}</td><td class="num">{{ pct(r.sla_ini_arri) }}</td><td class="num">{{ pct(r.sla_sol_arri) }}</td><td class="num">{{ pct(r.sla_total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>
      </section>

      <!-- ================= UNIDADES ================= -->
      <section v-show="activeMenu === 'unidades'" class="tab-body">
        <div class="kpi-cards">
          <div class="mini-kpi"><span>Utilización</span><strong>{{ num1(data.unidades.kpis.utilizacion) }}%</strong><small>Horas activas / disponibles</small></div>
          <div class="mini-kpi"><span>Eficiencia</span><strong>{{ num1(data.unidades.kpis.eficiencia) }}%</strong><small>Servicios / objetivo</small></div>
          <div class="mini-kpi"><span>Horas activas</span><strong>{{ num1(data.unidades.kpis.horas_activas) }}</strong><small>Horas trabajadas</small></div>
        </div>
        <div class="grid grid-2">
          <StatCard title="Servicios por unidad (top 15)"><v-chart :option="unidadServiciosOption" autoresize style="height:430px" /></StatCard>
          <StatCard title="% eficiencia por unidad (top 15)"><v-chart :option="unidadEficienciaOption" autoresize style="height:430px" /></StatCard>
        </div>
        <StatCard title="Detalle de desempeño por unidad">
          <div class="table-wrap tall">
            <table>
              <thead><tr>
                <th>Unidad</th><th>Tipo</th><th class="num">Servicios</th><th class="num">Objetivo</th>
                <th class="num">Eficiencia</th><th class="num">Utilización</th><th class="num">H. activas</th>
                <th class="num">Serv/hora</th><th class="num">Ventas</th><th class="num">Ticket</th>
                <th class="num">SLA ETA</th><th class="num">Sol→Arri</th><th class="num">Total</th>
              </tr></thead>
              <tbody>
                <tr v-for="(r, i) in data.unidades.detalle" :key="i" :class="{ 'total-row': r.unit_short_id === 'TOTAL' }">
                  <td>{{ r.unit_short_id }}</td><td>{{ r.tipo_unidad }}</td><td class="num">{{ r.servicios }}</td><td class="num">{{ fmtInt(r.objetivo_servicios) }}</td>
                  <td class="num">{{ pct(r.eficiencia_pct) }}</td><td class="num">{{ pct(r.utilizacion_pct) }}</td><td class="num">{{ num1(r.horas_activas) }}</td>
                  <td class="num">{{ num2(r.servicios_por_hora) }}</td><td class="num">{{ money(r.ventas) }}</td><td class="num">{{ money(r.ticket_promedio) }}</td>
                  <td class="num">{{ pct(mul100(r.sla_eta)) }}</td><td class="num">{{ minv(r.prom_sol_arri) }}</td><td class="num">{{ minv(r.prom_total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="caption">Eficiencia = servicios realizados / objetivo por tipo de unidad. Utilización = horas activas / horas disponibles.</p>
        </StatCard>
      </section>

      <!-- ================= OPERADORES ================= -->
      <section v-show="activeMenu === 'operadores'" class="tab-body">
        <StatCard title="Productividad por operador">
          <div class="table-wrap tall">
            <table>
              <thead><tr><th>Operador</th><th class="num">Servicios</th><th class="num">Ventas</th><th class="num">Ticket</th><th class="num">SLA ETA</th><th class="num">Sol→Arri</th><th class="num">Total</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in data.operadores.prod_operador" :key="i">
                  <td>{{ r.employee_ident }}</td><td class="num">{{ r.servicios }}</td><td class="num">{{ money(r.ventas) }}</td><td class="num">{{ money(r.ticket_promedio) }}</td>
                  <td class="num">{{ pct(r.sla_eta) }}</td><td class="num">{{ minv(r.prom_sol_arri) }}</td><td class="num">{{ minv(r.prom_total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>
        <StatCard title="Productividad por operador y tipo de unidad">
          <div class="table-wrap tall">
            <table>
              <thead><tr><th>Operador</th><th>Tipo unidad</th><th class="num">Servicios</th><th class="num">Ventas</th><th class="num">SLA ETA</th><th class="num">Sol→Arri</th><th class="num">Total</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in data.operadores.prod_operador_tipo" :key="i">
                  <td>{{ r.employee_ident }}</td><td>{{ r.tipo_unidad }}</td><td class="num">{{ r.servicios }}</td><td class="num">{{ money(r.ventas) }}</td>
                  <td class="num">{{ pct(r.sla_eta) }}</td><td class="num">{{ minv(r.prom_sol_arri) }}</td><td class="num">{{ minv(r.prom_total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>
      </section>

      <!-- ================= SLA / ETA ================= -->
      <section v-show="activeMenu === 'sla'" class="tab-body">
        <div class="grid grid-2">
          <StatCard title="Cumplimiento SLA ETA general"><v-chart :option="gaugeOption" autoresize style="height:400px" /></StatCard>
          <StatCard title="Cumplimiento SLA ETA por sucursal"><v-chart :option="etaSucursalOption" autoresize style="height:400px" /></StatCard>
        </div>
        <StatCard title="SLA ETA por sucursal y tipo">
          <div class="table-wrap">
            <table>
              <thead><tr><th>Sucursal</th><th>Tipo</th><th class="num">Servicios</th><th class="num">SLA ETA</th><th class="num">Sol→Arri</th><th class="num">Ini→Arri</th><th class="num">Total</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in data.sla_eta.eta_tipo" :key="i">
                  <td>{{ r.sucursal }}</td><td>{{ r.type }}</td><td class="num">{{ r.servicios }}</td><td class="num">{{ pct(r.sla_eta) }}</td>
                  <td class="num">{{ minv(r.prom_sol_arri) }}</td><td class="num">{{ minv(r.prom_ini_arri) }}</td><td class="num">{{ minv(r.prom_total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>
        <StatCard title="Resumen clientes (ETA)">
          <div class="table-wrap tall">
            <table>
              <thead><tr><th>Cliente</th><th class="num">Servicios</th><th class="num">Excelente</th><th class="num">A tiempo</th><th class="num">Tarde</th><th class="num">% Tarde</th><th class="num">ETA prom</th><th class="num">Arribo</th><th class="num">Ventas</th><th class="num">Ticket</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in data.sla_eta.cliente_resumen" :key="i">
                  <td>{{ r.cliente_cuenta }}</td><td class="num">{{ r.servicios }}</td><td class="num">{{ r.excelente }}</td><td class="num">{{ r.a_tiempo }}</td><td class="num">{{ r.tarde }}</td>
                  <td class="num">{{ pct(r.pct_tarde) }}</td><td class="num">{{ num2(r.eta_promedio) }}</td><td class="num">{{ minv(r.arribo_prom) }}</td><td class="num">{{ money(r.ventas) }}</td><td class="num">{{ money(r.ticket_promedio) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>
        <StatCard title="Detalle de órdenes">
          <div class="detalle-filters">
            <label>Turno:
              <select v-model="detTurno">
                <option value="Todos">Todos</option>
                <option v-for="t in detTurnoOptions" :key="t" :value="t">{{ t }}</option>
              </select>
            </label>
            <label>Clasificación ETA:
              <select v-model="detEta">
                <option value="Todos">Todos</option>
                <option v-for="c in detEtaOptions" :key="c" :value="c">{{ c }}</option>
              </select>
            </label>
            <label>Cliente:
              <select v-model="detCliente">
                <option value="Todos">Todos</option>
                <option v-for="c in detClienteOptions" :key="c" :value="c">{{ c }}</option>
              </select>
            </label>
            <label class="det-range">ETA índice:
              <input type="number" step="0.1" v-model.number="detEtaMin" placeholder="mín" />
              <span>–</span>
              <input type="number" step="0.1" v-model.number="detEtaMax" placeholder="máx" />
            </label>
            <label class="det-search">Buscar OT:
              <input type="text" v-model.trim="detSearch" placeholder="# de nota" />
            </label>
            <span class="det-count">{{ fmtInt(detalleFiltered.length) }} de {{ fmtInt(data.sla_eta.detalle.length) }}</span>
            <button class="btn-apply" type="button" @click="downloadDetalleCsv">Descargar CSV</button>
          </div>
          <div class="table-wrap tall">
            <table>
              <thead><tr><th>Turno</th><th>ETA</th><th>Cliente</th><th class="num">OT</th><th class="num">T. estimado</th><th class="num">ETA índice</th><th class="num">Arribo</th><th class="num">Total</th><th class="num">Venta</th><th>Sucursal</th><th>Tipo</th><th>Solicitud</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in detalleView" :key="i">
                  <td>{{ r.turno }}</td><td>{{ r.clasificacion_eta }}</td><td>{{ r.cliente_cuenta }}</td><td class="num">{{ r.note_number }}</td><td class="num">{{ r.expected_time }}</td>
                  <td class="num">{{ num2(r.eta_indice) }}</td><td class="num">{{ minv(r.min_sol_arri) }}</td><td class="num">{{ minv(r.min_total) }}</td><td class="num">{{ money(r.subtotal) }}</td>
                  <td>{{ r.sucursal }}</td><td>{{ r.type }}</td><td>{{ r.request_time }}</td>
                </tr>
                <tr v-if="!detalleFiltered.length"><td colspan="12" class="muted-cell">Sin órdenes con los filtros seleccionados.</td></tr>
              </tbody>
            </table>
          </div>
          <p class="caption" v-if="detalleFiltered.length > detalleLimit">Mostrando las primeras {{ detalleLimit }} órdenes. Descarga el CSV para ver todas las filtradas.</p>
        </StatCard>
      </section>

      <!-- ================= FINANZAS ================= -->
      <section v-show="activeMenu === 'finanzas'" class="tab-body">
        <StatCard title="Resumen financiero por sucursal y tipo">
          <div class="table-wrap tall">
            <table>
              <thead><tr><th>Sucursal</th><th>Tipo</th><th class="num">Ventas</th><th class="num">Servicios</th><th class="num">T. total prom</th><th class="num">Ticket</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in data.finanzas.resumen_fin" :key="i" :class="{ 'total-row': r.sucursal === 'TOTAL' }">
                  <td>{{ r.sucursal }}</td><td>{{ r.type }}</td><td class="num">{{ money(r.ventas) }}</td><td class="num">{{ r.servicios }}</td><td class="num">{{ minv(r.prom_total) }}</td><td class="num">{{ money(r.ticket_promedio) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </StatCard>
      </section>

      <!-- ================= REPORTES ================= -->
      <section v-show="activeMenu === 'reportes'" class="tab-body">
        <StatCard title="Base filtrada y enriquecida">
          <div class="report-head">
            <span>{{ fmtInt(data.reportes.rows.length) }} registros</span>
            <button class="btn-apply" type="button" @click="downloadCsv">Descargar CSV</button>
          </div>
          <div class="table-wrap tall">
            <table>
              <thead><tr><th v-for="c in data.reportes.columns" :key="c">{{ c }}</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in reporteRowsView" :key="i">
                  <td v-for="c in data.reportes.columns" :key="c">{{ r[c] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="caption" v-if="data.reportes.rows.length > reporteLimit">Mostrando los primeros {{ reporteLimit }} registros. Descarga el CSV para ver todos.</p>
        </StatCard>
      </section>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, provide, h } from 'vue'
import axios from 'axios'
import { THEME_KEY } from 'vue-echarts'
import StatCard from './primitives/StatCard.vue'

// Lightweight inline multi-select filter (checkbox dropdown)
const FilterGroup = {
  name: 'FilterGroup',
  props: {
    label: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const open = ref(false)
    const isAll = computed(() => props.modelValue.length === 0)
    const label = computed(() => {
      if (isAll.value) return `${props.label}: Todas`
      return `${props.label}: ${props.modelValue.length}`
    })
    function toggle(opt) {
      const set = new Set(props.modelValue.length ? props.modelValue : props.options)
      if (set.has(opt)) set.delete(opt); else set.add(opt)
      const arr = [...set]
      emit('update:modelValue', arr.length === props.options.length ? [] : arr)
    }
    function checked(opt) {
      return isAll.value ? true : props.modelValue.includes(opt)
    }
    return () => h('div', { class: 'fg' }, [
      h('button', { class: 'fg-btn', type: 'button', onClick: () => (open.value = !open.value) }, label.value),
      open.value
        ? h('div', { class: 'fg-menu' }, props.options.map((opt) =>
            h('label', { key: opt, class: 'fg-item' }, [
              h('input', { type: 'checkbox', checked: checked(opt), onChange: () => toggle(opt) }),
              h('span', {}, String(opt)),
            ])
          ))
        : null,
    ])
  },
}

const PALETTE = ['#2563eb', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4']

const menus = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'operacion', label: 'Operación' },
  { key: 'unidades', label: 'Unidades' },
  { key: 'operadores', label: 'Operadores' },
  { key: 'sla', label: 'SLA / ETA' },
  { key: 'finanzas', label: 'Finanzas' },
  { key: 'reportes', label: 'Reportes' },
]
const activeMenu = ref('resumen')
const metricSel = ref('sol_asig')

// dates default: last 7 days (Monterrey)
const todayMty = new Date().toLocaleString('en-CA', { timeZone: 'America/Monterrey' }).slice(0, 10)
const weekAgo = (() => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Monterrey' }))
  d.setDate(d.getDate() - 7)
  return d.toLocaleDateString('en-CA').slice(0, 10)
})()
const startDate = ref(weekAgo)
const endDate = ref(todayMty)

const data = ref(null)
const loading = ref(false)
const errorMsg = ref('')

const selGrupos = ref([])
const selSucursales = ref([])
const selTipos = ref([])
const selTiposUnidad = ref([])

const filtros = computed(() => {
  if (!data.value || !data.value.filtros) return null
  const f = data.value.filtros
  return { grupos: f.grupos, sucursales: f.sucursales, tipos: f.tipos, tiposUnidad: f.tipos_unidad }
})
const kpis = computed(() => (data.value ? data.value.kpis : null))
const op = computed(() => (data.value ? data.value.operacion : null))
const isEmpty = computed(() => data.value && data.value.empty)

const reporteLimit = 400
const reporteRowsView = computed(() => (data.value ? data.value.reportes.rows.slice(0, reporteLimit) : []))

// SLA "Detalle de órdenes" filters
const detTurno = ref('Todos')
const detEta = ref('Todos')
const detCliente = ref('Todos')
const detSearch = ref('')
const detEtaMin = ref(null)
const detEtaMax = ref(null)
const detalleLimit = 500
const slaDetalle = computed(() => (data.value && data.value.sla_eta ? data.value.sla_eta.detalle : []))
const detTurnoOptions = computed(() => [...new Set(slaDetalle.value.map((r) => r.turno).filter(Boolean))].sort())
const detEtaOptions = computed(() => [...new Set(slaDetalle.value.map((r) => r.clasificacion_eta).filter(Boolean))].sort())
const detClienteOptions = computed(() => [...new Set(slaDetalle.value.map((r) => r.cliente_cuenta).filter(Boolean))].sort())
const detalleFiltered = computed(() => {
  const q = (detSearch.value || '').toLowerCase()
  return slaDetalle.value.filter((r) => {
    if (detTurno.value !== 'Todos' && r.turno !== detTurno.value) return false
    if (detEta.value !== 'Todos' && r.clasificacion_eta !== detEta.value) return false
    if (detCliente.value !== 'Todos' && r.cliente_cuenta !== detCliente.value) return false
    const hasMin = detEtaMin.value !== null && detEtaMin.value !== ''
    const hasMax = detEtaMax.value !== null && detEtaMax.value !== ''
    if (hasMin || hasMax) {
      if (r.eta_indice == null) return false
      if (hasMin && r.eta_indice < detEtaMin.value) return false
      if (hasMax && r.eta_indice > detEtaMax.value) return false
    }
    if (q && !String(r.note_number ?? '').toLowerCase().includes(q)) return false
    return true
  })
})
const detalleView = computed(() => detalleFiltered.value.slice(0, detalleLimit))

function apiBase() { return process.env.API_DIRECTORY || '' }

async function fetchData() {
  loading.value = true
  errorMsg.value = ''
  try {
    const params = { start: startDate.value, end: endDate.value }
    if (selGrupos.value.length) params.grupos = selGrupos.value.join(',')
    if (selSucursales.value.length) params.sucursales = selSucursales.value.join(',')
    if (selTipos.value.length) params.tipos = selTipos.value.join(',')
    if (selTiposUnidad.value.length) params.tipos_unidad = selTiposUnidad.value.join(',')
    const r = await axios.get(`${apiBase()}/dashboard/indicadores-data`, {
      params,
      headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
    })
    data.value = r.data
  } catch (e) {
    if (e.response && e.response.status === 403) errorMsg.value = 'No tienes permisos para ver Indicadores.'
    else errorMsg.value = 'No se pudieron cargar los indicadores.'
  } finally {
    loading.value = false
  }
}

/* ───── formatters ───── */
function money(v) { return (Number(v) || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }) }
function fmtInt(v) { return (Number(v) || 0).toLocaleString('es-MX') }
function num1(v) { return v == null ? '—' : (Number(v)).toFixed(1) }
function num2(v) { return v == null ? '—' : (Number(v)).toFixed(2) }
function pct(v) { return v == null ? '—' : `${Number(v).toFixed(1)}%` }
function minv(v) { return v == null ? '—' : `${Number(v).toFixed(1)} min` }
function mul100(v) { return v == null ? null : Number(v) * 100 }
function abbr(v) {
  const n = Number(v) || 0
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}k`
  return `$${n.toFixed(0)}`
}

/* ───── theme ───── */
const isDark = ref(true)
try { const s = localStorage.getItem('indicadoresTheme'); if (s) isDark.value = s === 'dark' } catch (e) { /* ignore */ }
const themeClass = computed(() => (isDark.value ? 'theme-dark' : 'theme-light'))
function toggleTheme() {
  isDark.value = !isDark.value
  try { localStorage.setItem('indicadoresTheme', isDark.value ? 'dark' : 'light') } catch (e) { /* ignore */ }
}
const chartFont = 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
function buildChartTheme(dark) {
  const axisLabel = dark ? '#94a3b8' : '#475569'
  const axisLine = dark ? 'rgba(148,163,184,0.30)' : 'rgba(15,23,42,0.18)'
  const splitLine = dark ? 'rgba(148,163,184,0.10)' : 'rgba(15,23,42,0.07)'
  const axis = {
    axisLine: { lineStyle: { color: axisLine } },
    axisTick: { lineStyle: { color: axisLine } },
    axisLabel: { color: axisLabel },
    splitLine: { lineStyle: { color: splitLine } },
  }
  return {
    color: PALETTE,
    backgroundColor: 'transparent',
    textStyle: { color: dark ? '#cbd5e1' : '#334155', fontFamily: chartFont },
    title: { textStyle: { color: dark ? '#e2e8f0' : '#0f172a' }, subtextStyle: { color: dark ? '#94a3b8' : '#64748b' } },
    categoryAxis: axis,
    valueAxis: axis,
    legend: { textStyle: { color: dark ? '#cbd5e1' : '#334155' } },
    tooltip: {
      backgroundColor: dark ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)',
      borderColor: dark ? 'rgba(148,163,184,0.25)' : 'rgba(15,23,42,0.12)',
      borderWidth: 1,
      textStyle: { color: dark ? '#e2e8f0' : '#0f172a' },
    },
  }
}
provide(THEME_KEY, computed(() => buildChartTheme(isDark.value)))

/* ═══════════════ CHART OPTIONS ═══════════════ */
const EMPTY = {}

// --- RESUMEN ---
const ventasSucursalTipoOption = computed(() => {
  const r = data.value?.resumen?.ventas_sucursal_tipo
  if (!r) return EMPTY
  return stackedHBar(r.labels, r.series, (v) => (v < 40000 ? '' : '$' + Number(v).toLocaleString()))
})
const serviciosSucursalTipoOption = computed(() => {
  const r = data.value?.resumen?.servicios_sucursal_tipo
  if (!r) return EMPTY
  return stackedHBar(r.labels, r.series, (v) => (v < 3 ? '' : String(v)))
})
function stackedHBar(labels, series, labelFmt) {
  return {
    color: PALETTE,
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0 },
    grid: { left: '3%', right: '5%', top: '6%', bottom: '14%', containLabel: true },
    xAxis: { type: 'value', splitLine: { show: true } },
    yAxis: { type: 'category', data: labels, axisTick: { show: false } },
    series: series.map((s) => ({
      name: s.name, type: 'bar', stack: 'total', barWidth: 18, data: s.data,
      label: { show: true, position: 'inside', color: '#fff', fontWeight: 'bold', fontSize: 11, formatter: (p) => labelFmt(p.value) },
    })),
  }
}
const ticketHeatmapOption = computed(() => {
  const r = data.value?.resumen?.ticket_heatmap
  if (!r) return EMPTY
  return {
    tooltip: { position: 'top', formatter: (p) => p.name + '<br/>' + p.value[3] },
    grid: { height: '72%', top: '6%', left: '4%', right: '6%', bottom: '12%', containLabel: true },
    xAxis: { type: 'category', data: r.tipos, splitArea: { show: true }, axisLabel: { rotate: 30, interval: 0 }, axisTick: { show: false } },
    yAxis: { type: 'category', data: r.sucursales, splitArea: { show: true }, axisTick: { show: false } },
    visualMap: { show: false, min: 0, max: r.max || 1, dimension: 2, inRange: { color: ['#dcfce7', '#86efac', '#fde68a', '#fdba74', '#fca5a5'] } },
    series: [{
      type: 'heatmap', data: r.data,
      label: { show: true, formatter: (p) => p.value[3], fontWeight: 'bold', fontSize: 11, color: '#0f172a' },
      itemStyle: { borderColor: '#f1f5f9', borderWidth: 1 },
    }],
  }
})
const serviciosDiaOption = computed(() => {
  const r = data.value?.resumen?.servicios_dia
  if (!r) return EMPTY
  return {
    color: PALETTE,
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', top: '8%', bottom: '18%', containLabel: true },
    xAxis: { type: 'category', data: r.map((x) => x.fecha), axisLabel: { rotate: 90 } },
    yAxis: { type: 'value' },
    series: [{ name: 'Servicios', type: 'line', smooth: true, areaStyle: {}, data: r.map((x) => x.servicios) }],
  }
})
const heatDiaHoraOption = computed(() => {
  const r = data.value?.resumen?.heat_dia_hora
  if (!r) return EMPTY
  return {
    tooltip: { position: 'top' },
    grid: { height: '60%', top: '10%', containLabel: true },
    xAxis: { type: 'category', data: r.horas_labels, splitArea: { show: true }, axisLabel: { interval: 0, rotate: 90 } },
    yAxis: { type: 'category', data: r.dias, splitArea: { show: true } },
    visualMap: { type: 'piecewise', orient: 'horizontal', left: 'center', bottom: 0, itemWidth: 12, itemHeight: 10, pieces: heatPieces(r.max) },
    series: [{ type: 'heatmap', data: r.data, label: { show: false }, itemStyle: { borderColor: '#f1f5f9', borderWidth: 1 } }],
  }
})
function heatPieces(maxHeat) {
  if (!maxHeat || maxHeat <= 0) return [{ lte: 0, label: 'Sin servicios', color: '#ffffff' }]
  const p = (x) => Math.round(maxHeat * x)
  return [
    { lte: 0, label: 'Sin servicios', color: '#ffffff' },
    { gt: 0, lte: p(0.2), label: 'Muy bajo', color: '#1e3a8a' },
    { gt: p(0.2), lte: p(0.4), label: 'Bajo', color: '#2563eb' },
    { gt: p(0.4), lte: p(0.6), label: 'Medio', color: '#22c55e' },
    { gt: p(0.6), lte: p(0.8), label: 'Alto', color: '#facc15' },
    { gt: p(0.8), label: 'Muy alto', color: '#ef4444' },
  ]
}
const serviciosHoraTipoOption = computed(() => lineMulti(data.value?.resumen?.servicios_hora_tipo))
const serviciosHoraFechaOption = computed(() => lineMulti(data.value?.resumen?.servicios_hora_fecha))
function lineMulti(r) {
  if (!r) return EMPTY
  return {
    color: PALETTE,
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, type: 'scroll' },
    grid: { left: '3%', right: '4%', top: '6%', bottom: '18%', containLabel: true },
    xAxis: { type: 'category', data: r.horas_labels, axisLabel: { rotate: 90 } },
    yAxis: { type: 'value' },
    series: r.series.map((s) => ({ name: s.name, type: 'line', smooth: true, data: s.data })),
  }
}

// --- OPERACIÓN ---
const tiemposTurnoOption = computed(() => {
  const mc = op.value?.metric_charts?.[metricSel.value]
  if (!mc) return EMPTY
  const series = mc.series.map((s) => ({
    name: s.name, type: 'line', smooth: true, symbol: 'circle', symbolSize: 7, data: s.data,
    label: { show: true, formatter: '{c} min', fontSize: 11 },
  }))
  series.push({ name: 'Meta', type: 'line', data: mc.fechas.map(() => mc.meta), symbol: 'none', lineStyle: { type: 'dashed', width: 2, color: '#ef4444' } })
  return {
    color: PALETTE,
    title: { text: `${mc.label} por día y turno`, subtext: 'Promedio en minutos', left: 'center' },
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0 },
    grid: { left: '6%', right: '5%', top: '18%', bottom: '14%', containLabel: true },
    xAxis: { type: 'category', data: mc.fechas, axisLabel: { rotate: 30 } },
    yAxis: { type: 'value', axisLabel: { formatter: '{value} min' } },
    series,
  }
})
const slaHoraOption = computed(() => {
  const r = op.value?.sla_por_hora
  if (!r) return EMPTY
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0 },
    grid: { left: '8%', right: '5%', top: '8%', bottom: '18%', containLabel: true },
    xAxis: { type: 'category', data: r.horas_labels, axisLabel: { rotate: 90, fontSize: 11 } },
    yAxis: [
      { type: 'value', name: 'SLA %', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
      { type: 'value', name: 'Servicios', splitLine: { show: false } },
    ],
    series: [
      { name: 'SLA ETA', type: 'bar', data: r.sla_eta, label: { show: true, position: 'top', formatter: '{c}%', fontSize: 9 }, itemStyle: { color: '#10b981', borderRadius: [4, 4, 0, 0] } },
      { name: 'Servicios', type: 'line', yAxisIndex: 1, smooth: true, data: r.servicios, lineStyle: { width: 3, color: '#2563eb' }, itemStyle: { color: '#2563eb' } },
      { name: 'Meta 80%', type: 'line', data: r.horas_labels.map(() => 80), symbol: 'none', lineStyle: { type: 'dashed', width: 2, color: '#ef4444' } },
    ],
  }
})
const slaHeatOption = computed(() => {
  const r = op.value?.sla_heat
  if (!r) return EMPTY
  return {
    tooltip: {
      position: 'top',
      formatter: (p) => {
        const sla = p.value[2]; const serv = p.value[3]
        if (sla === null) return p.name + '<br/>Sin servicios'
        return p.name + '<br/>SLA ETA: ' + Number(sla).toFixed(1) + '%<br/>Servicios: ' + serv
      },
    },
    grid: { height: '60%', top: '8%', left: '8%', right: '5%', bottom: '20%', containLabel: true },
    xAxis: { type: 'category', data: r.horas_labels, splitArea: { show: true }, axisLabel: { interval: 0, rotate: 90, fontSize: 11 } },
    yAxis: { type: 'category', data: r.dias, splitArea: { show: true } },
    visualMap: {
      type: 'piecewise', orient: 'horizontal', left: 'center', bottom: 0, min: 0, max: 100, dimension: 2,
      pieces: [
        { value: null, label: 'Sin servicios', color: '#ffffff' },
        { gte: 0, lt: 60, label: '<60%', color: '#ef4444' },
        { gte: 60, lt: 75, label: '60-75%', color: '#f97316' },
        { gte: 75, lt: 85, label: '75-85%', color: '#facc15' },
        { gte: 85, lt: 95, label: '85-95%', color: '#22c55e' },
        { gte: 95, lte: 100, label: '95-100%', color: '#16a34a' },
      ],
    },
    series: [{
      type: 'heatmap', data: r.data,
      label: { show: true, formatter: (p) => (p.value[2] === null || p.value[3] === 0 ? '' : Number(p.value[2]).toFixed(0) + '%'), fontSize: 9, fontWeight: 'bold', color: '#0f172a' },
      itemStyle: { borderColor: '#f1f5f9', borderWidth: 1 },
    }],
  }
})
const headOperadoresOption = computed(() => headBar(op.value?.headcount?.operadores_turno, 'Operadores'))
const headUnidadesOption = computed(() => headBar(op.value?.headcount?.unidades_turno, 'Unidades'))
function headBar(r, yName) {
  if (!r) return EMPTY
  return {
    color: PALETTE,
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0 },
    grid: { left: '8%', right: '5%', top: '6%', bottom: '16%', containLabel: true },
    xAxis: { type: 'category', data: r.fechas, axisLabel: { rotate: 45, fontSize: 10 } },
    yAxis: { type: 'value', name: yName },
    series: r.series.map((s) => ({ name: s.name, type: 'bar', data: s.data, label: { show: true, position: 'top' } })),
  }
}
const ocupOperadoresOption = computed(() => {
  const r = op.value?.ocupacion
  if (!r) return EMPTY
  return ocupLine(r, 'Operadores ocupados', r.operadores_ocupados)
})
const ocupUnidadesOption = computed(() => {
  const r = op.value?.ocupacion
  if (!r) return EMPTY
  return ocupLine(r, 'Unidades ocupadas', r.unidades_ocupadas)
})
function ocupLine(r, name, ocupData) {
  return {
    color: PALETTE,
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0 },
    grid: { left: '8%', right: '5%', top: '6%', bottom: '18%', containLabel: true },
    xAxis: { type: 'category', data: r.hora_labels, axisLabel: { rotate: 90, fontSize: 11 } },
    yAxis: { type: 'value' },
    series: [
      { name: 'Solicitudes', type: 'line', smooth: true, data: r.solicitudes },
      { name: 'Terminadas', type: 'line', smooth: true, data: r.terminadas },
      { name, type: 'line', smooth: true, lineStyle: { width: 4 }, data: ocupData },
    ],
  }
}

// --- UNIDADES ---
const unidadServiciosOption = computed(() => {
  const r = data.value?.unidades?.top_servicios
  if (!r) return EMPTY
  return simpleBar(r.labels, r.servicios || r.data, '#2563eb')
})
const unidadEficienciaOption = computed(() => {
  const r = data.value?.unidades?.top_eficiencia
  if (!r) return EMPTY
  return simpleBar(r.labels, r.data, '#10b981', '{c}%')
})
function simpleBar(labels, dataArr, color, labelFmt) {
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: '8%', right: '5%', top: '6%', bottom: '16%', containLabel: true },
    xAxis: { type: 'category', data: labels, axisLabel: { rotate: 45, fontSize: 11 } },
    yAxis: { type: 'value' },
    series: [{ type: 'bar', barWidth: 22, data: dataArr, label: { show: true, position: 'top', fontSize: 10, formatter: labelFmt || '{c}' }, itemStyle: { color, borderRadius: [4, 4, 0, 0] } }],
  }
}

// --- SLA / ETA ---
const gaugeOption = computed(() => {
  const v = data.value?.sla_eta?.gauge
  if (v == null) return EMPTY
  return {
    series: [{
      type: 'gauge', startAngle: 210, endAngle: -30, min: 0, max: 100, progress: { show: true },
      axisLine: { lineStyle: { width: 18, color: [[0.8, '#ef4444'], [0.9, '#f59e0b'], [1, '#22c55e']] } },
      detail: { valueAnimation: true, formatter: '{value}%', fontSize: 28 },
      data: [{ value: v }],
    }],
  }
})
const etaSucursalOption = computed(() => {
  const r = data.value?.sla_eta?.eta_sucursal
  if (!r) return EMPTY
  return {
    color: PALETTE,
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', top: '8%', bottom: '18%', containLabel: true },
    xAxis: { type: 'category', data: r.labels, axisLabel: { rotate: 45 } },
    yAxis: { type: 'value' },
    series: [{ name: 'SLA ETA %', type: 'bar', data: r.data, label: { show: true, formatter: '{c}%' } }],
  }
})

/* ───── CSV export ───── */
function exportCsv(cols, rows, filename) {
  const esc = (v) => {
    if (v == null) return ''
    const s = String(v)
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const lines = [cols.join(',')]
  for (const r of rows) lines.push(cols.map((c) => esc(r[c])).join(','))
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function downloadCsv() {
  if (!data.value) return
  exportCsv(data.value.reportes.columns, data.value.reportes.rows, `indicadores_${startDate.value}_${endDate.value}.csv`)
}

const DETALLE_CSV_COLS = [
  'turno', 'clasificacion_eta', 'cliente_cuenta', 'note_number', 'expected_time',
  'eta_indice', 'min_sol_arri', 'min_total', 'subtotal', 'sucursal', 'type',
  'request_time', 'assigned_at', 'start_time', 'arrival_time', 'contact_time', 'end_time',
]
function downloadDetalleCsv() {
  if (!data.value) return
  exportCsv(DETALLE_CSV_COLS, detalleFiltered.value, `detalle_ordenes_${startDate.value}_${endDate.value}.csv`)
}

onMounted(fetchData)
</script>

<style scoped>
.ind-root {
  --row-h: 30px;
  font-family: 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  position: relative;
  min-height: 100vh;
  padding: 18px 22px 48px;
  color: var(--text);
  background: var(--bg1);
  transition: background .35s ease, color .35s ease;
  overflow-x: hidden;
}
.theme-dark {
  color-scheme: dark;
  --bg1: #070b16; --text: #e8edf6; --text-muted: #9aa7bd; --text-dim: #6b7891;
  --surface-solid: #121a2d; --border: rgba(148,163,184,.12); --border-strong: rgba(148,163,184,.22);
  --accent: #38bdf8; --accent-grad: linear-gradient(180deg,#38bdf8,#818cf8); --accent-grad-h: linear-gradient(90deg,#38bdf8,#818cf8);
  --card-bg: linear-gradient(180deg, rgba(21,29,48,.80), rgba(13,19,34,.80)); --card-border: rgba(148,163,184,.14);
  --card-border-hover: rgba(56,189,248,.50); --card-shadow: 0 10px 30px rgba(2,6,18,.45); --card-shadow-hover: 0 18px 44px rgba(2,6,18,.62);
  --card-blur: blur(10px); --card-radius: 16px; --card-pad: 14px; --card-title: #e8edf6;
  --card-topline: linear-gradient(90deg, transparent, rgba(56,189,248,.6), transparent);
  --input-bg: rgba(9,14,28,.7); --input-border: rgba(148,163,184,.20);
  --table-head-bg: rgba(11,17,33,.96); --row-even: rgba(148,163,184,.05); --row-hover: rgba(56,189,248,.10);
  --kpi-bg: rgba(30,41,59,.42); --glass: rgba(15,22,40,.66);
}
.theme-light {
  color-scheme: light;
  --bg1: #eaeef6; --text: #0f172a; --text-muted: #475569; --text-dim: #94a3b8;
  --surface-solid: #fff; --border: rgba(15,23,42,.08); --border-strong: rgba(15,23,42,.14);
  --accent: #0284c7; --accent-grad: linear-gradient(180deg,#0ea5e9,#6366f1); --accent-grad-h: linear-gradient(90deg,#0ea5e9,#6366f1);
  --card-bg: rgba(255,255,255,.90); --card-border: rgba(15,23,42,.08); --card-border-hover: rgba(2,132,199,.40);
  --card-shadow: 0 6px 20px rgba(15,23,42,.07); --card-shadow-hover: 0 16px 38px rgba(15,23,42,.14);
  --card-blur: blur(6px); --card-radius: 16px; --card-pad: 14px; --card-title: #0f172a;
  --card-topline: linear-gradient(90deg, transparent, rgba(14,165,233,.5), transparent);
  --input-bg: #fff; --input-border: #d8dde6;
  --table-head-bg: rgba(255,255,255,.97); --row-even: rgba(15,23,42,.03); --row-hover: rgba(2,132,199,.07);
  --kpi-bg: #f8fafc; --glass: rgba(255,255,255,.72);
}
.ambient { position: fixed; inset: 0; z-index: 0; pointer-events: none; }
.theme-dark .ambient {
  background:
    radial-gradient(900px 520px at 12% -8%, rgba(56,189,248,.16), transparent 60%),
    radial-gradient(820px 520px at 92% 0%, rgba(129,140,248,.16), transparent 60%),
    linear-gradient(180deg, #070b16 0%, #0a1020 100%);
}
.theme-light .ambient {
  background:
    radial-gradient(900px 500px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(820px 520px at 92% -4%, rgba(99,102,241,.10), transparent 60%),
    linear-gradient(180deg, #eaeef6 0%, #f4f7fc 100%);
}
.ind-root > *:not(.ambient) { position: relative; z-index: 1; }

.ind-header {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  padding: 12px 16px; border-radius: 18px; background: var(--glass); border: 1px solid var(--border);
  box-shadow: var(--card-shadow); backdrop-filter: blur(14px);
}
.brand { display: flex; align-items: center; gap: 12px; cursor: pointer; }
.logo { height: 38px; filter: drop-shadow(0 4px 12px rgba(56,189,248,.35)); }
.brand-text { display: flex; flex-direction: column; line-height: 1.15; }
.brand-text h1 { margin: 0; font-size: 19px; font-weight: 700; background: var(--accent-grad-h); -webkit-background-clip: text; background-clip: text; color: transparent; }
.brand-sub { font-size: 11px; color: var(--text-muted); }
.header-right { display: flex; align-items: center; gap: 10px; }
.btn-refresh {
  display: inline-flex; align-items: center; gap: 8px; padding: 9px 16px; border-radius: 12px; border: 1px solid transparent;
  color: #fff; background: var(--accent-grad-h); font-weight: 600; font-family: inherit; cursor: pointer;
  box-shadow: 0 8px 22px rgba(56,189,248,.35);
}
.btn-refresh:disabled { opacity: .6; cursor: not-allowed; }
.theme-toggle {
  display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 12px;
  border: 1px solid var(--border-strong); background: var(--kpi-bg); color: var(--text); cursor: pointer;
}
.theme-toggle svg { width: 18px; height: 18px; }

.ind-root .controls {
  position: relative; z-index: 50;
  display: flex; gap: 14px; align-items: center; flex-wrap: wrap; font-size: 13px; color: var(--text-muted);
  padding: 12px 16px; border-radius: 14px; background: var(--glass); border: 1px solid var(--border); margin-top: 14px;
}
.controls label { display: inline-flex; align-items: center; gap: 8px; font-weight: 500; }
.controls input[type="date"] {
  padding: 7px 10px; font-size: 13px; font-family: inherit; color: var(--text); background: var(--input-bg);
  border: 1px solid var(--input-border); border-radius: 9px; accent-color: var(--accent);
}
.btn-apply {
  padding: 8px 14px; border-radius: 10px; border: 1px solid var(--border-strong); background: var(--kpi-bg);
  color: var(--text); font-weight: 600; font-family: inherit; cursor: pointer;
}
.btn-apply:hover { border-color: var(--accent); }
.filters { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }

/* filter group (rendered by inline component) */
:deep(.fg) { position: relative; }
:deep(.fg-btn) {
  padding: 7px 12px; border-radius: 999px; border: 1px solid var(--border-strong); background: var(--kpi-bg);
  color: var(--text-muted); font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
}
:deep(.fg-menu) {
  position: absolute; z-index: 30; top: calc(100% + 6px); left: 0; min-width: 200px; max-height: 280px; overflow: auto;
  background: var(--surface-solid); border: 1px solid var(--border-strong); border-radius: 12px; padding: 8px;
  box-shadow: 0 16px 40px rgba(2,6,23,.35); display: flex; flex-direction: column; gap: 4px;
}
:deep(.fg-item) { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--text); padding: 3px 4px; cursor: pointer; }

.hero-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-top: 14px; }
.hero-tile {
  position: relative; padding: 14px 16px; border-radius: 16px; background: var(--card-bg); border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow); backdrop-filter: var(--card-blur); overflow: hidden; display: flex; flex-direction: column;
}
.hero-tile::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--accent-grad); }
.hero-label { font-size: 11px; font-weight: 600; letter-spacing: .5px; text-transform: uppercase; color: var(--text-muted); }
.hero-value { margin-top: 6px; font-size: 24px; font-weight: 800; line-height: 1.1; color: var(--text); font-variant-numeric: tabular-nums; }
.hero-sub { font-size: 11px; color: var(--text-dim); margin-top: 3px; }

.tabs {
  display: flex; gap: 6px; margin: 16px 0; flex-wrap: wrap; padding: 6px; border-radius: 16px;
  background: var(--glass); border: 1px solid var(--border); width: fit-content; max-width: 100%; backdrop-filter: blur(10px);
}
.tab {
  padding: 9px 16px; border: 1px solid transparent; border-radius: 12px; background: transparent; color: var(--text-muted);
  font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; transition: color .18s, background .18s;
}
.tab:hover { color: var(--text); background: var(--row-hover); }
.tab.active { color: #fff; background: var(--accent-grad-h); box-shadow: 0 8px 22px rgba(56,189,248,.35); }

.tab-body { display: flex; flex-direction: column; gap: 14px; }
.state-msg { padding: 40px; text-align: center; color: var(--text-muted); font-size: 15px; }
.state-msg.error { color: #ef4444; }

.grid { display: grid; gap: 12px; }
.grid-2 { grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); }
.grid-3 { grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }

.kpi-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
.mini-kpi {
  display: flex; flex-direction: column; gap: 3px; padding: 14px 16px; border-radius: 14px; background: var(--card-bg);
  border: 1px solid var(--card-border); box-shadow: var(--card-shadow);
}
.mini-kpi span { font-size: 11px; text-transform: uppercase; letter-spacing: .5px; color: var(--text-muted); font-weight: 600; }
.mini-kpi strong { font-size: 22px; color: var(--text); font-variant-numeric: tabular-nums; }
.mini-kpi small { font-size: 11px; color: var(--text-dim); }

.metric-select { margin-bottom: 8px; }
.metric-select select {
  padding: 6px 10px; border-radius: 9px; border: 1px solid var(--input-border); background: var(--input-bg); color: var(--text); font-family: inherit;
}

.table-wrap { overflow: auto; border: 1px solid var(--border); border-radius: 10px; max-height: 460px; }
.table-wrap.tall { max-height: 620px; }
.table-wrap table { width: 100%; border-collapse: collapse; font-size: 12px; color: var(--text); }
.table-wrap th, .table-wrap td { padding: 7px 9px; border-bottom: 1px solid var(--border); text-align: left; white-space: nowrap; }
.table-wrap td.num, .table-wrap th.num { text-align: right; }
.table-wrap thead th { position: sticky; top: 0; background: var(--table-head-bg); color: var(--text-muted); font-weight: 700; z-index: 1; backdrop-filter: blur(6px); }
.table-wrap tbody tr:nth-child(even) td { background: var(--row-even); }
.table-wrap tbody tr:hover td { background: var(--row-hover); }
.table-wrap tr.total-row td { font-weight: 800; color: var(--accent); }

.caption { font-size: 12px; color: var(--text-dim); margin: 8px 2px 0; }
.report-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; font-size: 13px; color: var(--text-muted); }

.detalle-filters {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; font-size: 12.5px; color: var(--text-muted);
}
.detalle-filters label { display: inline-flex; align-items: center; gap: 6px; font-weight: 500; }
.detalle-filters select, .detalle-filters input {
  padding: 6px 9px; border-radius: 9px; border: 1px solid var(--input-border); background: var(--input-bg); color: var(--text);
  font-family: inherit; font-size: 12.5px; max-width: 220px;
}
.det-search input { width: 120px; }
.det-range input { width: 68px; }
.det-range span { color: var(--text-dim); }
.det-count { color: var(--text-dim); margin-left: auto; }
.muted-cell { color: var(--text-dim); text-align: center; padding: 16px; }
</style>
